<script lang="ts" setup>
import { ref, watch, computed } from 'vue';
import Button from '@pet/base.button/index.vue';
import { useIntersectionObserver } from '@vueuse/core';
import XImage from '@pet/base.image/index.vue';
import SeckillBar from './SeckillBar.vue';
import type { CardLogNormalParam, SpikeItem } from './types';
import PriceButton from './PriceButton.vue';
import { getPrice, getPercentBySoldNumber, convertToPercentage, formatSoldNumber } from '../utils/price';

interface Props {
    /**
     * 自定义样式
     */
    customClsName?: string;
    /**
     * 商品id
     */
    itemId: number;
    /**
     * 商品图角标
     */
    itemBanner?: string;
    /**
     * 图片
     */
    itemImage?: string;
    /**
     * 商品名称
     */
    itemName?: string;
    /**
     * 标签
     */
    tags?: string[];
    /**
     * 已售库存
     */
    soldStock?: number;
    /**
     * 商品原价
     */
    itemPrice?: number;
    /**
     * 商品售价
     */
    discountPrice?: number;
    /**
     * 补贴力度
     */
    disCountPercent?: number;
    /**
     *跳转url
     */
    itemOrderUrlKrn?: string;
    /**
     * 品牌名
     */
    chainStoreName?: string;
    /**
     * poi的数量
     */
    itemPoiCount?: number;
    /**
     * poi的名字
     */
    poiName?: string;
    /**
     * 按钮的属性
     */
    marketBtnProps?: any;
    /**
     * 优惠价格-icon
     */
    disCountImg?: string;
    /**
     * 优惠价格
     */
    reducePriceTag?: string;
}

const PREFIX = 'card';

const taskItemRoot = ref<HTMLDivElement | null>(null);
const targetIsVisible = ref(false);

const props = withDefaults(defineProps<Props>(), {
    itemPoiCount: 0,
    disCountPercent: 0,
    itemPrice: 0,
    discountPrice: 0,
    soldStock: 0,
    itemImage: '',
    itemOrderUrlKrn: '',
});

const { stop } = useIntersectionObserver(taskItemRoot, (arr) => {
    if (arr?.[0]?.isIntersecting) {
        targetIsVisible.value = true;
    }
});

const emit = defineEmits<{
    (e: 'cardClick', params: SpikeItem): void;
    (e: 'btnClick', params: SpikeItem): void;
    (e: 'logEvent', params: CardLogNormalParam): void;
}>();

const cardParam = computed(() => {
    return {
        itemId: props.itemId,
        itemBanner: props.itemBanner!,
        itemImgUrl: props.itemImage!,
        itemTitle: props.itemName!,
        tags: props.tags ?? [],
        soldStock: props.soldStock,
        itemMarketPrice: props.itemPrice,
        discountPrice: props.discountPrice,
        discount: props.disCountPercent,
        itemOrderUrlKrn: props.itemOrderUrlKrn,
    };
});

const onItemClick = (event: Event) => {
    emit('cardClick', cardParam.value);
    emit('logEvent', {
        logType: 'cardClick',
        logParam: cardParam.value,
    });
};

const onButtonClick = (event: Event) => {
    emit('btnClick', cardParam.value);
};

watch(
    () => targetIsVisible.value,
    (visible) => {
        if (visible) {
            emit('logEvent', {
                logType: 'cardShow',
                logParam: cardParam.value,
            });
        }
    },
);

const itemMarketPrice = computed(() => {
    return getPrice(props.itemPrice);
});

const itemDiscountPrice = computed(() => {
    return getPrice(props.discountPrice);
});

const itemDisCountPercent = computed(() => {
    return convertToPercentage(props.disCountPercent);
});

const seckillProps = computed(() => {
    return {
        textList: `已抢${formatSoldNumber(props.soldStock) as string}件`,
        sellPercent: getPercentBySoldNumber(props.soldStock),
    };
});
</script>

<template>
    <div ref="taskItemRoot" :class="`${PREFIX} ${customClsName}`" @click="onItemClick">
        <div class="card-left">
            <div class="card-left-items">
                <XImage :src="itemImage" class="card-left-items-img" loading="lazy" loading-ctrl="self" />
                <slot v-if="$slots.banner"></slot>
                <div v-else-if="itemBanner" class="card-left-items-banner">
                    {{ itemBanner }}
                </div>
            </div>
            <SeckillBar v-bind="seckillProps" class="card-left-seckill" />
        </div>

        <div :class="`${PREFIX}-itemsContent`">
            <div :class="`${PREFIX}-itemsContent-itemsContentTop`">
                <div :class="`${PREFIX}-itemsContent-itemsContentTop-itemName`">
                    <p :class="`${PREFIX}-itemsContent-itemsContentTop-itemName-row-one`">{{ itemName }}</p>
                </div>
                <div :class="`${PREFIX}-tags`">
                    <div v-for="(item, index) in tags" :key="index" :class="`${PREFIX}-tags-tagItem`">
                        {{ item }}
                    </div>
                </div>
            </div>
            <slot v-if="$slots.priceCompare" name="priceCompare"></slot>
            <template v-else>
                <div :class="`${PREFIX}-itemsContent-price`">
                    <div :class="`${PREFIX}-itemsContent-price-text`">
                        原价¥<span :class="`${PREFIX}-itemsContent-price-text-origin`">{{ itemMarketPrice }}</span>
                    </div>
                    <div v-if="disCountPercent" :class="`${PREFIX}-itemsContent-price-discount`">
                        <div :class="`${PREFIX}-itemsContent-price-discount-percent`">
                            {{ itemDisCountPercent }}
                        </div>
                        <div :class="`${PREFIX}-itemsContent-price-discount-desc`">补贴</div>
                    </div>
                </div>
            </template>
            <div :class="`${PREFIX}-itemsContent-bugBtn`">
                <div v-if="!!reducePriceTag" :class="`${PREFIX}-itemsContent-bugBtn-discountPrice`">
                    <img :src="disCountImg" />
                    <span>{{ reducePriceTag }}</span>
                </div>
                <Button animation @click="onButtonClick">
                    <div :class="`${PREFIX}-itemsContent-bugBtn-content`">
                        <span :class="`${PREFIX}-itemsContent-bugBtn-content-desc`">补贴价</span>
                        <span :class="`${PREFIX}-itemsContent-bugBtn-content-unit`">¥</span>
                        <span :class="`${PREFIX}-itemsContent-bugBtn-content-price`">{{ itemDiscountPrice }}</span>
                    </div>
                </Button>
            </div>
        </div>
    </div>
</template>

<style lang="stylus" scoped>
.card
    display flex
    background-color #fff
    border-radius 16px

    padding 16px
    height 184px
    box-sizing border-box

    &-left
        width 128px
        display flex
        flex-direction column
        justify-content space-between

        &-items
            position relative
            width 100%
            height 128px

            &-img
                width 100%
                height 100%
                background-size cover
                border-radius 8px

                :deep(.image-main){
                    object-fit cover
                }

            &-banner
                position absolute
                top 0
                left 0
                width 52px
                height 18px
                display flex
                justify-content center
                align-items center
                border-top-left-radius 8px
                border-bottom-right-radius 8px
                color: #FFF;
                font-family PingFang SC
                font-size 10px
                font-weight 500
                line-height normal
                background linear-gradient(45deg, #FE3666 0%, #FF62A8 100%)

        &-seckill
            width 100%


    &-tags
        margin-top 8px
        display flex

        &-tagItem
            font-family PingFang SC
            font-size 10px
            font-weight 400
            line-height 11px
            padding 2px 4px
            border-radius 3px
            border 1px solid rgba(0, 55, 79, 0.12)
            margin-right 4px
            color rgba(0,55,79,0.5)

            &:first-child
                color #FE3666
                border 1px solid rgba(254,54,102,0.12)

    &-itemsContent
        flex-grow 1
        padding-left 12px
        display flex
        flex-direction column
        font-family PingFang SC
        color #222222
        overflow-x hidden
        justify-content space-between

        &-price
            margin-top 30px
            width 167px
            height 33px
            background url('../assets/priceCutCurve.png') no-repeat
            background-size 167px 33px
            position relative


            &-text
                position absolute
                top -17px
                left 3px
                color rgba(0,55,79,0.5)
                font-family PingFang SC
                font-size 10px
                font-weight 400
                line-height 14px

                &-origin
                    display inline-block
                    text-decoration line-through

            &-discount
                width 67px
                height 43px
                background url('../assets/downArr.png') no-repeat
                background-size 100% 100%
                position absolute
                top -22px
                left 68px
                display flex
                flex-direction column
                align-items center
                color: #FFF;
                font-family PingFang SC
                font-size 12px
                font-weight 600
                line-height 12px

                &-percent
                    margin-top 8px

        &-bugBtn
            display flex
            flex-direction column
            margin-top 8px
            position relative

            :deep(.btn-size-l){
                height 35px
                width 210px
            }

            :deep(.btn-primary){
                background url('../assets/btnBkg.png')
                background-size 100% 100%
            }

            :deep(.btn-primary .btn-words) {
                position absolute
                width 100%
                height 100%
            }

            &-content
                position absolute
                top 7px
                left 28px
                color: #FE3666;
                font-family: PingFang SC;
                font-weight: 400;

                &-desc
                    font-size: 12px;
                    line-height: 12px;

                &-unit
                    display inline-block
                    margin-left 8px
                    font-size: 15px;
                    line-height: 15px;

                &-price
                    font-size: 20px;
                    line-height: 20px;
                    font-weight: 500;
                    margin-left: 2px;

            &-discountPrice
                display flex
                align-items center
                font-size 12px
                margin-bottom 3px
                line-height 17px
                color #fe3666
                font-weight 500

                > img
                    height 14px
                    background-size cover

                > span
                    font-weight 500

        &-itemsContentTop
            flex-direction column
            justify-content space-between

            &-itemName
                font-size 17px
                font-weight 600
                overflow hidden
                max-width 202px
                line-height 21px

                &-row-one
                    max-width 100%
                    margin 0
                    overflow hidden
                    text-overflow ellipsis
                    white-space nowrap
</style>
